import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const THEMES = {
  DARK: {
    bg: "bg-black",
    card: "bg-white/10",
    text: "text-white",
    subText: "text-gray-300",
    accent: "text-yellow-300"
  },
  LIGHT: {
    bg: "bg-gray-100",
    card: "bg-white",
    text: "text-gray-900",
    subText: "text-gray-600",
    accent: "text-blue-600"
  },
  MIDNIGHT: {
    bg: "bg-slate-900",
    card: "bg-slate-800",
    text: "text-white",
    subText: "text-slate-300",
    accent: "text-cyan-300"
  },
  NEON: {
    bg: "bg-black",
    card: "bg-black/60 border border-cyan-500",
    text: "text-cyan-200",
    subText: "text-cyan-400",
    accent: "text-pink-400"
  },
  PASTEL: {
    bg: "bg-pink-50",
    card: "bg-white",
    text: "text-pink-900",
    subText: "text-pink-600",
    accent: "text-rose-500"
  }
};

const HAFHDiary = () => {
  const authState = useSelector(state => state.authReducer);

  const [userName] = useState(authState.user.name);
  const [tasks, setTasks] = useState([]);

  const [fetchData, { loading }] = useFetch();
  const { diaryType } = useParams();

  const [currentPage, setCurrentPage] = useState(0);
  const [isTTSOn, setIsTTSOn] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  const [theme, setTheme] = useState("DARK");
  const [showFilter, setShowFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    from: "",
    to: ""
  });

  const sliderRef = useRef(null);

  // ================= FETCH =================
  const fetchTasks = useCallback(() => {
    const config = {
      url: "/hafhtodo/mydiary",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId: authState.user._id, diaryType }
    };

    fetchData(config, { showSuccessToast: false })
      .then(data => setTasks(data.tasks || []));
  }, [authState.token, fetchData, authState.user._id, diaryType]);

  useEffect(() => {
    if (!authState.isLoggedIn) return;
    fetchTasks();
  }, [authState.isLoggedIn, fetchTasks]);

  // ================= SORT =================
  const sortedTasks = [...tasks].sort(
      (a, b) =>
        new Date(b.todoDate || b.taskDate || 0) -
        new Date(a.todoDate || a.taskDate || 0)
    );

  const filteredTasks = sortedTasks.filter(task => {
      if (!dateFilter.from && !dateFilter.to) return true;

      const taskDate = new Date(task.todoDate || task.taskDate);

      if (dateFilter.from && taskDate < new Date(dateFilter.from)) return false;
      if (dateFilter.to && taskDate > new Date(dateFilter.to)) return false;

      return true;
    });

    const totalPages = filteredTasks.length;

    const currentTheme = THEMES[theme];

    const getReadableDate = (dateStr) => {
      if (!dateStr) return "";

      const date = new Date(dateStr);
      const today = new Date();

      const diffTime = today.setHours(0,0,0,0) - new Date(date).setHours(0,0,0,0);
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";

      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    };

    // ================= SPEECH =================
    const speakText = (text) => {
    if (!window.speechSynthesis) return;

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.lang = "en-IN";

    utterance.onend = () => {
      if (!autoScroll) return;

      setCurrentPage(prev => {
        const next = prev + 1;

        if (next < totalPages) {
          return next;
        } else {
          setIsTTSOn(false);
          return prev;
        }
      });
    };

    window.speechSynthesis.speak(utterance);
  };

  // ================= TTS TOGGLE =================
  const handleTTSToggle = () => {
    if (isTTSOn) {
      window.speechSynthesis.cancel();
      setIsTTSOn(false);
      return;
    }

    setIsTTSOn(true);

    const task = filteredTasks[currentPage];
    if (task) speakText(`${task.title}. ${task.description}`);
  };

  // ================= AUTO SPEAK =================
  useEffect(() => {
    if (!isTTSOn) return;

    const task = filteredTasks[currentPage];
    if (!task) return;

    const timer = setTimeout(() => {
      const readableDate = getReadableDate(task.todoDate || task.taskDate);
      speakText(`${readableDate}. ${task.description}`);
    }, 250);

    return () => clearTimeout(timer);
  }, [currentPage, isTTSOn, filteredTasks]);

  // ================= RENDER =================
  const renderTask = (task) => {
    if (!task) return null;

    return (
      <div className={`p-3 h-[90vh] overflow-auto ${currentTheme.bg}`}>

        <div className={`${currentTheme.card} backdrop-blur-md rounded-2xl p-5 shadow-lg h-full overflow-auto`}>

          <div className={`flex justify-between text-xs mb-3 ${currentTheme.subText}`}>
            <span>📘 {userName}</span>
            <span>🏠 Hostel Diary</span>
          </div>

          <div className={`text-sm mb-2 ${currentTheme.accent}`}>
            📅 {new Date(task.todoDate || task.taskDate).toLocaleDateString()}
          </div>

          <h2 className={`text-xl font-bold mb-3 ${currentTheme.accent}`}>
            {task.title}
          </h2>

          <p className={`text-sm leading-relaxed ${currentTheme.text}`}>
            {task.description}
          </p>

          {task.remarks && (
            <p className={`mt-4 text-xs ${currentTheme.subText}`}>
              💬 {task.remarks}
            </p>
          )}

        </div>
      </div>
    );
  };

  // ================= UI =================
  return (
    <div className={`mx-auto max-w-[700px] py-4 min-h-screen ${currentTheme.bg}`}>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* TOP CONTROL BAR */}
          <div className="flex flex-col gap-2 mb-3">
            <div className="flex flex-col gap-2 mb-2">
              <div className="flex items-center justify-between px-2 py-2 bg-black/40 text-white rounded-md">
                <button
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 0))}
                  className="px-2 py-1 text-xs bg-gray-700 rounded"
                >
                  ⬅
                </button>
                <div className={`text-xs px-2 py-1 rounded
                  ${theme === "LIGHT" || theme === "PASTEL"
                    ? "text-gray-900 bg-gray-200"
                    : "text-white bg-white/10"
                  }`}
                >
                  {currentPage + 1}/{filteredTasks.length}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage(p => Math.min(p + 1, filteredTasks.length - 1))
                  }
                  className="px-2 py-1 text-xs bg-gray-700 rounded"
                >
                  ➡
                </button>
                <button
                  onClick={() => setShowFilter(p => !p)}
                  className="px-2 py-1 text-xs bg-indigo-600 rounded"
                >
                  📅
                </button>
                <button
                  onClick={handleTTSToggle}
                  className={`px-2 py-1 text-xs rounded ${
                    isTTSOn ? "bg-green-500" : "bg-gray-700"
                  }`}
                >
                  {isTTSOn ? "🔊" : "🎧"}
                </button>
                <button
                  onClick={() => setAutoScroll(p => !p)}
                  className={`px-2 py-1 text-xs rounded ${
                    autoScroll ? "bg-blue-500" : "bg-gray-700"
                  }`}
                >
                  ⚡
                </button>
              </div>

              {/* FILTER PANEL (collapsible) */}
              {showFilter && (
                <div className="flex gap-2 px-2">

                  <input
                    type="date"
                    value={dateFilter.from}
                    onChange={(e) =>
                      setDateFilter(prev => ({ ...prev, from: e.target.value }))
                    }
                    className="text-xs p-1 rounded bg-white text-black"
                  />

                  <input
                    type="date"
                    value={dateFilter.to}
                    onChange={(e) =>
                      setDateFilter(prev => ({ ...prev, to: e.target.value }))
                    }
                    className="text-xs p-1 rounded bg-white text-black"
                  />

                  <button
                    onClick={() => setDateFilter({ from: "", to: "" })}
                    className="text-xs px-2 py-1 bg-red-500 rounded text-white"
                  >
                    Reset
                  </button>

                </div>
              )}
            </div>
            <div className="flex justify-between items-center bg-black/40 text-white px-3 py-2 rounded-sm">

              <button
                onClick={handleTTSToggle}
                className={`w-32 lg:w-40 px-2 py-1 rounded-full text-xs font-semibold
                  ${isTTSOn ? "bg-green-500" : "bg-gray-700"}`}
              >
                {isTTSOn ? "🔊 Listening" : "🎧 Listen"}
              </button>

              <button
                onClick={() => setAutoScroll(p => !p)}
                className={`w-32 lg:w-48 px-1 py-1 rounded-lg text-xs font-semibold
                  ${autoScroll ? "bg-blue-500" : "bg-gray-700"}`}
              >
                {autoScroll ? "⚡ Auto Scroll ON" : "⏸ OFF"}
              </button>
              {/* THEME SELECTOR */}
              <div className="flex justify-end">
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="text-xs px-2 py-1 rounded-lg bg-black text-white outline-none"
                >
                  <option value="DARK">🌙 Dark (Default)</option>
                  <option value="LIGHT">☀️ Light</option>
                  <option value="MIDNIGHT">🌌 Midnight Blue</option>
                  <option value="NEON">⚡ Neon Cyber</option>
                  <option value="PASTEL">🌸 Soft Pastel</option>
                </select>
              </div>
            </div>
          </div>
          {/* DIARY VIEW */}
          {filteredTasks.length > 0 && renderTask(filteredTasks[currentPage])}
        </>
      )}
    </div>
  );
};

export default HAFHDiary;